import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@shared/api';
import { dateTime, idr, num, relative } from '@shared/format';
import { refreshBalance } from '@shared/hooks';
import { CopyIcon } from '../components/CopyIcon';
import { TopupSuccess } from '../components/TopupSuccess';
import { Alert, Badge, Card, Empty, Icon, KeyValue, Loading, PageHeader } from '../components/ui';
import '../styles/billing.css';

interface Balance {
  available_balance: number;
  reserved_balance: number;
  total_balance: number;
  updated_at: string;
}

interface Payment {
  id: string;
  external_payment_id: string | null;
  amount: number;
  bonus_amount: number;
  credited_amount: number;
  method: string;
  status: string;
  payment_url: string | null;
  /** Our checkout page. */
  direct?: boolean;
  paid_at: string | null;
  created_at: string;
}

interface TopupResult {
  payment_url: string | null;
  external_payment_id: string | null;
  direct?: boolean;
  payment_id: string;
  amount: number;
  bonus_amount: number;
  credited_amount: number;
}

type TopupMode = 'live' | 'test' | 'sandbox' | 'unavailable';

interface TopupOptions {
  mode: TopupMode;
  min_amount: number;
  max_amount: number;
  denominations: Array<{ amount: number; bonus_amount: number }>;
  methods: Array<{ id: string; min_amount: number; max_amount: number }>;
}

const MODE_COPY: Record<TopupMode, { badge: string | null; text: string }> = {
  live: { badge: null, text: 'Bayar lewat QRIS, e-wallet, virtual account, atau kartu. Saldo bertambah otomatis setelah pembayaran terkonfirmasi.' },
  test: { badge: 'Mode uji', text: 'Gateway pembayaran dalam mode uji, tidak ada uang yang ditarik. Saldo bertambah setelah pembayaran uji dikonfirmasi.' },
  sandbox: { badge: 'Simulasi', text: 'Pembayaran masih simulasi, tidak ada uang yang ditarik. Saldo bertambah setelah pembayaran dikonfirmasi.' },
  unavailable: { badge: null, text: '' },
};

const POLL_MS = 4_000;
const POLL_TRIES = 45;

/** Checks after Xendit redirect. */
function usePaymentReturn(onSettled: (externalId: string) => Promise<void>): { id: string; status: string } | null {
  // read once, then clean URL
  const [externalId] = useState(() => new URLSearchParams(window.location.search).get('payment'));
  const [state, setState] = useState<{ id: string; status: string } | null>(null);

  useEffect(() => {
    if (!externalId) return;
    window.history.replaceState(null, '', window.location.pathname);
    let alive = true;
    let tries = 0;
    let timer = 0;
    const check = async () => {
      try {
        const result = await api<{ status: string }>(`/v1/payments/${encodeURIComponent(externalId)}/refresh`, { method: 'POST' });
        if (!alive) return;
        setState({ id: externalId, status: result.status });
        if (result.status === 'PENDING' && ++tries < POLL_TRIES) timer = window.setTimeout(check, POLL_MS);
        else await onSettled(externalId);
      } catch {
        if (alive) setState(null);
      }
    };
    void check();
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [externalId]);

  return state;
}

const METHOD_LABELS: Record<string, string> = {
  QRIS: 'QRIS',
  EWALLET_GOPAY: 'GoPay',
  EWALLET_OVO: 'OVO',
  VA_BCA: 'Virtual Account BCA',
  VA_BNI: 'Virtual Account BNI',
  VA_MANDIRI: 'Virtual Account Mandiri',
  CARD: 'Kartu kredit/debit',
};

/** 30000 -> Rp30rb. */
function shortRupiah(value: number): string {
  const [unit, size] = value >= 1_000_000 ? ['jt', 1_000_000] : ['rb', 1_000];
  return `Rp${(value / size).toLocaleString('id-ID', { maximumFractionDigits: 1 })}${unit}`;
}

/** Bonus via quote; formula stays server-side. */
function useBonus(options: TopupOptions | null, amount: number): number {
  const preset = options?.denominations.find((d) => d.amount === amount);
  const [quoted, setQuoted] = useState<{ amount: number; bonus: number } | null>(null);
  useEffect(() => {
    if (!options || preset || amount < options.min_amount || amount > options.max_amount) return;
    let alive = true;
    const timer = window.setTimeout(() => {
      api<{ bonus_amount: number }>(`/v1/wallet/topup-quote?amount=${amount}`)
        .then((q) => alive && setQuoted({ amount, bonus: q.bonus_amount }))
        .catch(() => alive && setQuoted({ amount, bonus: 0 }));
    }, 250);
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [options, preset, amount]);
  if (preset) return preset.bonus_amount;
  return quoted?.amount === amount ? quoted.bonus : 0;
}

const METHOD_SHORT: Record<string, string> = {
  QRIS: 'QRIS',
  VA_BCA: 'VA BCA',
  VA_BNI: 'VA BNI',
  VA_MANDIRI: 'VA Mandiri',
  EWALLET_OVO: 'OVO',
  EWALLET_GOPAY: 'GoPay',
  CARD: 'Kartu',
  MANUAL: 'Manual',
};

const LOW_BALANCE = 10_000;

const STATUS_LABELS: Partial<Record<string, string>> = {
  PENDING: 'Menunggu',
  PAID: 'Lunas',
  FAILED: 'Gagal',
  EXPIRED: 'Kedaluwarsa',
  REFUNDED: 'Dikembalikan',
};

export function Wallet() {
  const [balance, setBalance] = useState<Balance | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [monthCost, setMonthCost] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [celebrate, setCelebrate] = useState<{ payment: Payment; balance: number } | null>(null);
  const seen = useRef<Map<string, string> | null>(null);
  const celebrated = useRef(new Set<string>());
  const paymentsRef = useRef<Payment[]>([]);

  const load = async (returnedId?: string) => {
    try {
      const [b, p, u] = await Promise.all([
        api<Balance>('/v1/wallet/balance'),
        api<{ data: Payment[] }>('/v1/payments'),
        // burn rate optional
        api<{ totals: { cost_idr: number } }>('/v1/usage/summary').catch(() => null),
      ]);
      setBalance(b);
      setPayments(p.data);
      paymentsRef.current = p.data;
      setMonthCost(u ? u.totals.cost_idr : null);

      // pending turned paid
      const previous = seen.current;
      const paid = p.data.find(
        (x) =>
          x.status === 'PAID' &&
          !celebrated.current.has(x.id) &&
          (previous?.get(x.id) === 'PENDING' || (returnedId !== undefined && x.external_payment_id === returnedId)),
      );
      seen.current = new Map(p.data.map((x) => [x.id, x.status]));
      if (paid) {
        celebrated.current.add(paid.id);
        setCelebrate({ payment: paid, balance: b.available_balance });
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const returned = usePaymentReturn(async (externalId) => {
    await load(externalId);
    refreshBalance();
  });
  const closeCelebrate = useCallback(() => setCelebrate(null), []);

  // back from paying tab
  useEffect(() => {
    let last = 0;
    const sync = async () => {
      if (document.visibilityState !== 'visible' || Date.now() - last < 5_000) return;
      const pending = paymentsRef.current.filter((x) => x.status === 'PENDING' && x.external_payment_id).slice(0, 3);
      if (pending.length === 0) return;
      last = Date.now();
      await Promise.all(
        pending.map((x) => api(`/v1/payments/${encodeURIComponent(x.external_payment_id!)}/refresh`, { method: 'POST' }).catch(() => null)),
      );
      await load();
      refreshBalance();
    };
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', sync);
    return () => {
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);
  const [checking, setChecking] = useState<string | null>(null);

  // webhook may lag
  const checkStatus = async (externalId: string) => {
    setChecking(externalId);
    try {
      await api(`/v1/payments/${encodeURIComponent(externalId)}/refresh`, { method: 'POST' });
      await load();
      refreshBalance();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setChecking(null);
    }
  };

  if (loading) return <Loading label="Memuat saldo" />;
  if (!balance) return <Alert kind="error">{error ?? 'Saldo Anda gagal dimuat.'}</Alert>;

  const low = balance.available_balance < LOW_BALANCE;
  const pending = payments.filter((p) => p.status === 'PENDING').length;
  const perDay = monthCost !== null ? monthCost / 30 : null;
  const runway = perDay && perDay > 0 ? Math.floor(balance.available_balance / perDay) : null;

  return (
    <>
      <PageHeader
        title="Saldo"
        subtitle="Saldo prabayar dalam Rupiah. Biaya setiap request dipotong dari sini sesuai pemakaian sebenarnya."
        actions={
          <Link className="btn" to="/transactions">
            <Icon name="swap" size={16} />
            Lihat transaksi
          </Link>
        }
      />

      {celebrate && <TopupSuccess payment={celebrate.payment} balance={celebrate.balance} onClose={closeCelebrate} />}
      {error && <Alert kind="error">{error}</Alert>}
      {returned?.status === 'PAID' && <Alert kind="success">Pembayaran diterima. Saldo Anda sudah bertambah.</Alert>}
      {returned?.status === 'PENDING' && (
        <Alert kind="info">Menunggu konfirmasi pembayaran. Saldo bertambah otomatis begitu pembayaran diterima, halaman ini ikut diperbarui.</Alert>
      )}
      {(returned?.status === 'EXPIRED' || returned?.status === 'FAILED') && (
        <Alert kind="warn">Pembayaran belum selesai atau sudah kedaluwarsa. Buat tagihan baru bila masih ingin mengisi saldo.</Alert>
      )}
      {low && (
        <Alert kind={balance.available_balance <= 0 ? 'error' : 'warn'}>
          {balance.available_balance <= 0
            ? 'Saldo habis. Semua request ditolak sampai saldo diisi lagi.'
            : `Saldo tinggal ${idr(balance.available_balance)}. Request yang perkiraan biayanya melebihi saldo akan ditolak.`}
        </Alert>
      )}

      <div className="bl-wallet">
        <Card framed className="bl-balance bl-w-balance">
          <div className="bl-metric-label">Saldo tersedia</div>
          <div className={low ? 'bl-balance-value low' : 'bl-balance-value'} data-testid="available-balance">
            {idr(balance.available_balance)}
          </div>
          <div className="bl-metric-sub" style={{ marginTop: 6 }}>
            Total {idr(balance.total_balance)} · diperbarui {relative(balance.updated_at)}
          </div>

          <div className="bl-facts">
            <div>
              <span>Ditahan</span>
              <strong>{idr(balance.reserved_balance)}</strong>
              <small>Dilepas setelah request selesai</small>
            </div>
            <div>
              <span>Pemakaian per hari</span>
              <strong>{perDay === null ? '—' : idr(perDay)}</strong>
              <small>Rata-rata 30 hari terakhir</small>
            </div>
            <div>
              <span>Perkiraan cukup untuk</span>
              <strong>{runway === null ? '—' : `± ${num(runway)} hari`}</strong>
              <small>{runway === null ? 'Belum ada pemakaian' : 'Dengan laju saat ini'}</small>
            </div>
          </div>
        </Card>

        <div className="bl-w-topup">
          <TopupPanel
            onDone={async () => {
              await load();
              refreshBalance();
            }}
          />
        </div>

        <Card
          className="bl-w-history"
          title="Riwayat top up"
          description={`${num(payments.length)} pembayaran${pending > 0 ? ` · ${pending} menunggu` : ''}`}
        >
          {payments.length === 0 ? (
            <Empty icon="card">Belum ada top up. Pembayaran yang Anda buat akan muncul di sini.</Empty>
          ) : (
            <div className="table-wrap">
              <table className="bl-payments">
                <thead>
                  <tr>
                    <th>Tanggal</th>
                    <th>Metode</th>
                    <th className="num">Jumlah</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment) => (
                    <tr key={payment.id}>
                      <td className="nowrap bl-p-date">
                        <div className="bl-stack">
                          <span>{dateTime(payment.created_at)}</span>
                          {payment.paid_at && <span className="bl-sub">Lunas {dateTime(payment.paid_at)}</span>}
                        </div>
                      </td>
                      <td className="bl-p-method">
                        <div className="bl-stack">
                          <span className="secondary">{METHOD_SHORT[payment.method] ?? payment.method}</span>
                          {payment.external_payment_id && (
                            <span className="bl-id dim" title={payment.external_payment_id}>
                              <span className="truncate" style={{ maxWidth: 150 }}>
                                {payment.external_payment_id}
                              </span>
                              <CopyIcon value={payment.external_payment_id} label="Salin referensi" />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="num bl-p-amt">
                        <div className="bl-stack" style={{ justifyItems: 'end' }}>
                          <span className="bl-strong">{idr(payment.amount)}</span>
                          {payment.bonus_amount > 0 && <span className="bl-sub bl-bonus">Bonus +{idr(payment.bonus_amount)}</span>}
                        </div>
                      </td>
                      <td className="bl-p-status">
                        <Badge status={payment.status}>{STATUS_LABELS[payment.status] ?? payment.status}</Badge>
                      </td>
                      <td className="bl-p-act" style={{ textAlign: 'right' }}>
                        {payment.status === 'PENDING' && (
                          <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                            {payment.external_payment_id && (
                              <button className="btn btn-sm btn-ghost" onClick={() => void checkStatus(payment.external_payment_id!)} disabled={checking === payment.external_payment_id}>
                                {checking === payment.external_payment_id ? <span className="spinner" /> : 'Cek status'}
                              </button>
                            )}
                            {payment.direct && payment.external_payment_id ? (
                              <Link className="btn btn-sm" to={`/checkout/${encodeURIComponent(payment.external_payment_id)}`}>
                                Bayar
                              </Link>
                            ) : (
                              payment.payment_url && (
                                <a className="btn btn-sm" href={payment.payment_url} target="_blank" rel="noreferrer">
                                  Bayar
                                  <Icon name="external" size={13} />
                                </a>
                              )
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

function TopupPanel({ onDone }: { onDone: () => Promise<void> }) {
  const navigate = useNavigate();
  const [options, setOptions] = useState<TopupOptions | null>(null);
  const [amount, setAmount] = useState(100_000);
  const [method, setMethod] = useState('QRIS');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TopupResult | null>(null);

  useEffect(() => {
    api<TopupOptions>('/v1/wallet/topup-options')
      .then(setOptions)
      .catch((err: Error) => setError(err.message));
  }, []);

  const methods = useMemo(() => (options?.methods ?? []).filter((m) => METHOD_LABELS[m.id]), [options]);
  const bonus = useBonus(options, amount);
  const eligible = (id: string) => {
    const m = methods.find((x) => x.id === id);
    return !!m && amount >= m.min_amount && amount <= m.max_amount;
  };

  // fall back when ineligible
  useEffect(() => {
    const fallback = methods.find((m) => eligible(m.id));
    if (fallback && !eligible(method)) setMethod(fallback.id);
  });

  if (!options) return <Card title="Isi saldo">{error ? <Alert kind="error">{error}</Alert> : <Loading label="Memuat pilihan" />}</Card>;

  if (options.mode === 'unavailable') {
    return (
      <Card title="Isi saldo">
        <Alert kind="info">
          Isi saldo online belum tersedia. Hubungi <a href="mailto:halo@partnerai.id">halo@partnerai.id</a> untuk menambah saldo.
        </Alert>
      </Card>
    );
  }
  const copy = MODE_COPY[options.mode];

  const tooLow = amount < options.min_amount;
  const tooHigh = amount > options.max_amount;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await api<TopupResult>('/v1/wallet/topup', { method: 'POST', body: { amount, method } });
      // QR/VA on our page
      if (created.direct && created.external_payment_id) {
        navigate(`/checkout/${encodeURIComponent(created.external_payment_id)}`);
        return;
      }
      // cards, e-wallets: Xendit
      if ((options.mode === 'live' || options.mode === 'test') && created.payment_url) {
        window.location.assign(created.payment_url);
        return;
      }
      setResult(created);
      await onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const finish = async () => {
    setResult(null);
    await onDone();
  };

  return (
    <Card
      title={
        <span className="row" style={{ gap: 8 }}>
          Isi saldo
          {copy.badge && <Badge tone="warn">{copy.badge}</Badge>}
        </span>
      }
      description={copy.text}
    >
      {result ? (
        <>
          <Alert kind="success">
            Tagihan {idr(result.amount)} dibuat. Saldo bertambah {idr(result.credited_amount)} setelah pembayarannya dikonfirmasi.
          </Alert>
          <KeyValue
            items={[
              [
                'ID',
                <span className="bl-id" key="id">
                  <span className="truncate" style={{ maxWidth: 190 }}>{result.payment_id}</span>
                  <CopyIcon value={result.payment_id} label="Salin ID pembayaran" />
                </span>,
              ],
              ['Status', <Badge key="s" status="PENDING">Menunggu</Badge>],
            ]}
          />
          <div className="col mt">
            {result.payment_url && (
              <a className="btn btn-primary btn-block" href={result.payment_url} target="_blank" rel="noreferrer">
                Buka halaman pembayaran
                <Icon name="external" size={14} />
              </a>
            )}
            <button className="btn btn-block" onClick={finish}>
              Selesai
            </button>
          </div>
        </>
      ) : (
        <form onSubmit={submit}>
          {error && <Alert kind="error">{error}</Alert>}

          <div className="bl-topup-grid">
            <div className="field">
              <label htmlFor="topup-amount">Jumlah</label>
              <div className="bl-chips" role="group" aria-label="Pilihan jumlah">
                {options.denominations.map((d) => (
                  <button
                    type="button"
                    key={d.amount}
                    className={amount === d.amount ? 'bl-chip on' : 'bl-chip'}
                    onClick={() => setAmount(d.amount)}
                    aria-pressed={amount === d.amount}
                  >
                    <span>{num(d.amount)}</span>
                    {d.bonus_amount > 0 && <small className="bl-chip-bonus">+{shortRupiah(d.bonus_amount)} bonus</small>}
                  </button>
                ))}
              </div>
              <div className="bl-money">
                <input
                  id="topup-amount"
                  type="number"
                  min={options.min_amount}
                  max={options.max_amount}
                  step={1000}
                  value={amount || ''}
                  onChange={(e) => setAmount(Number(e.target.value))}
                  required
                />
              </div>
              <span className="field-hint" style={tooLow || tooHigh ? { color: 'var(--status-critical)' } : undefined}>
                {tooHigh
                  ? `Maksimal ${idr(options.max_amount)} per tagihan.`
                  : `Minimal ${idr(options.min_amount)}. Makin besar isi saldo, makin besar bonusnya.`}
              </span>
            </div>

            <div className="field" style={{ marginBottom: 0 }}>
              <span className="label">Metode pembayaran</span>
              <div className="bl-methods" role="radiogroup" aria-label="Metode pembayaran">
                {methods.map((m) => {
                  const ok = eligible(m.id);
                  return (
                    <label key={m.id} className={`bl-method${method === m.id ? ' on' : ''}${ok ? '' : ' off'}`}>
                      <input type="radio" name="method" value={m.id} checked={method === m.id} disabled={!ok} onChange={() => setMethod(m.id)} />
                      {METHOD_LABELS[m.id]}
                      {!ok && <span className="bl-fee">{amount < m.min_amount ? `Mulai ${idr(m.min_amount)}` : `Maks ${idr(m.max_amount)}`}</span>}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="bl-receipt">
            <div>
              <span>Anda bayar</span>
              <span>{idr(tooLow ? 0 : amount)}</span>
            </div>
            <div>
              <span>Bonus saldo</span>
              <span className={bonus ? 'bl-bonus' : undefined}>{bonus ? `+${idr(bonus)}` : '—'}</span>
            </div>
            <div className="bl-receipt-total">
              <span>Saldo masuk</span>
              <strong>{idr(tooLow ? 0 : amount + bonus)}</strong>
            </div>
          </div>

          <button className="btn btn-primary btn-block btn-lg" disabled={busy || tooLow || tooHigh || !eligible(method)}>
            {busy ? <span className="spinner" /> : <Icon name="wallet" size={16} />}
            Bayar {idr(amount)}
          </button>
          <p className="field-hint bl-fineprint">Tanpa biaya tambahan. Bonus saldo tidak dapat diuangkan.</p>
        </form>
      )}
    </Card>
  );
}
